import { memo, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useGetChildTasksQuery, useUpdateChildSubTaskMutation, useUpdateChildTaskMutation, useUpdateChildTaskNoteMutation } from '@/services/child-api';
import { PlusSvg } from '@/assets/icons';
import Table from '@/components/table';
import Button from '@/components/button';
import EditModal from './edit-modal';
import AddModal from './add-modal';
import { TasksSvg, CheckSvg, TimesSvg, EditSvg } from '@/assets/icons';
import './task-list.css';
import { Input } from 'antd';

const TaskList = ({ childId, permissions }) => {
  const [visibleSubtasks, setVisibleSubtasks] = useState({});
  const [transformedData, setTransformedData] = useState([]);
  const [editingRow, setEditingRow] = useState(null);
  const [inputValues, setInputValues] = useState({});
  const textAreaRefs = useRef({});

  const toggleSubtasksVisibility = (taskId) => {
    setVisibleSubtasks((prevState) => ({
      ...prevState,
      [taskId]: !prevState[taskId],
    }));
  };
  const transformDataWithSubtasks = (data) => {
    return data.reduce((acc, task) => {
      acc.push({ ...task, isSubtask: false });

      if (task.hasSubTasks && task.subTasks && visibleSubtasks[task.childTaskId]) {
        task.subTasks.forEach((subtask) => {
          acc.push({ ...subtask, isSubtask: true, mainTaskId: task.childTaskId });
        });
      }

      return acc;
    }, []);
  };
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const pageNumber = queryParams.get('page') || 1;
  const { data, isFetching, refetch } = useGetChildTasksQuery({ childId, pageNumber });
  const [updateTask, { isLoading }] = useUpdateChildTaskMutation();
  const [updateTaskNote, { isNoteLoading }] = useUpdateChildTaskNoteMutation();
  const [updateSubTaskStatus] = useUpdateChildSubTaskMutation();

  const [isAddModalOpen, openAddModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState();

  useEffect(() => {
    const updatedData = transformDataWithSubtasks(data?.data || []);
    setTransformedData(updatedData);
  }, [data, visibleSubtasks]);

  const handleInputChange = (value, record) => {
    setInputValues((prev) => ({ ...prev, [record.childSubTaskId]: value }));
    record.assignment = value;
    //updateAssignment(value, record);
  };

  const updateStatus = async (task, status) => {
    await updateTask({
      childId,
      status: status,
      body: {
        type: { id: task.type.id },
        note: task.note,
      },
    }).unwrap();
    refetch();
  };

  const updateSubStatus = async (subTask, status) => {
    await updateSubTaskStatus({
      childId,
      typeId: subTask.type.id,
      assignment: subTask.assignment,
      status: status,
    }).unwrap();
    refetch();
  };

  const updateAssignment = (value, record) => {
    setTimeout(async () => {
      try {
        await updateSubTaskStatus({
          childId,
          typeId: record.type.id,
          assignment: value,
          status: record.status.code,
        }).unwrap();
      } catch (error) {
        console.error('Failed to update assignment:', error);
      }
    }, 5000);
  };

  const taskNoteEdit = async (record, note) => {
    await updateTaskNote({
      childId,
      id: record.childTaskId,
      body: {
        note: note
      }
    }).unwrap();
    setEditingRow(null);
    refetch();
  };

  const columns = [
    {
      title: 'Task',
      dataIndex: 'type',
      render: (value, record) => (
        <>
          {record.isSubtask ? (
            <div style={{ marginLeft: '25px' }}>{value.name}</div>
          ) : (
            <>
              {value.name}
              {record.hasSubTasks && (
                <div style={{ marginLeft: '15px' }}>
                  <Button type="link" onClick={() => toggleSubtasksVisibility(record.childTaskId)}>
                    {visibleSubtasks[record.childTaskId] ? 'Hide' : 'Show'} sub-tasks
                  </Button>
                </div>
              )}
            </>
          )}
        </>
      ),
    },
    {
      title: 'Note',
      dataIndex: 'note',
      render: (value, record) => {
        if (editingRow === record.childTaskId && permissions.adminStaffAndSuper) {
          return (
            <>
              <textarea
                ref={(el) => (textAreaRefs.current[record.key] = el)}
                defaultValue={value}
                style={{ width: '100%' }}
              />
              <Button type="primary" onClick={() => taskNoteEdit(record, textAreaRefs.current[record.key].value)}>
                Save
              </Button>
            </>
          );
        }

        return (
          <>
            {value}&nbsp;
            {!record.isSubtask && (
              <Button
                type="primary"
                style={{ width: 27, height: 27 }}
                icon={<EditSvg />}
                onClick={() => setEditingRow(record.childTaskId)} // Enter editing mode
              />
            )}
          </>
        );
      },
    },
    {
      title: permissions.adminStaffAndSuper && (
        <div style={{ textAlign: 'right' }}>
          <Button type="primary" icon={<PlusSvg fontSize="12" />} onClick={() => openAddModal(true)}>
            Add
          </Button>
        </div>
      ),
      width: '600px',
      render: (_, record) => {
        if (!record.isSubtask) {
          return (
            <div style={{ textAlign: 'right' }}>
              <button disabled={!permissions.adminStaffAndSuper}
                className={record.status.code === 'in-progress' ? 'selected' : 'notSelected'}
                onClick={() => updateStatus(record, 'in-progress')}
              >
                <TasksSvg />
                &nbsp; In Progress
              </button>
              <button disabled={!permissions.adminStaffAndSuper}
                className={record.status.code === 'not-applicable' ? 'selected' : 'notSelected'}
                onClick={() => updateStatus(record, 'not-applicable')}
              >
                N/A
              </button>
              <button disabled={!permissions.adminStaffAndSuper}
                className={record.status.code === 'complete' ? 'selected' : 'notSelected'}
                onClick={() => updateStatus(record, 'complete')}
              >
                <CheckSvg />
                &nbsp;Complete
              </button>
              <button disabled={!permissions.adminStaffAndSuper}
                className={record.status.code === 'rejected' ? 'selected' : 'notSelected'}
                onClick={() => updateStatus(record, 'rejected')}
              >
                <TimesSvg />
                &nbsp;Rejected
              </button>
              <button disabled={!permissions.adminStaffAndSuper}
                className={record.status.code === 'on-hold' ? 'selected' : 'notSelected'}
                onClick={() => updateStatus(record, 'on-hold')}
              >
                On Hold
              </button>
            </div>
          );
        } else {
          return (
            <div style={{ textAlign: 'right' }}>
              {/* {record.assignment && */
                (record.type.id === 1 || record.type.id === 2 ? (
                  <>
                    Assignment:&nbsp;
                    <Input
                      value={inputValues[record.childSubTaskId] || record.assignment}
                      style={{ width: 170, height: 28, marginRight: 5 }}
                      onChange={(e) => handleInputChange(e.target.value, record)}
                    />
                  </>
                ) : (record.type.id > 2) ? (
                  <>
                    Note:&nbsp;
                    <Input
                      value={inputValues[record.childSubTaskId] || record.assignment}
                      style={{ width: 170, height: 28, marginRight: 5 }}
                      onChange={(e) => handleInputChange(e.target.value, record)}
                    />
                  </>
                ) : null)}
              {/* <Button onClick={() => updateAssignment(???, record)}>
                <SaveSvg />
              </Button> */}
              <button disabled={!permissions.adminStaffAndSuper}
                className={record.status.code === 'open' ? 'selected' : 'notSelected'}
                onClick={() => updateSubStatus(record, 'open')}
              >
                Open
              </button>
              <button disabled={!permissions.adminStaffAndSuper}
                className={record.status.code === 'complete' ? 'selected' : 'notSelected'}
                onClick={() => updateSubStatus(record, 'complete')}
              >
                Complete
              </button>
              <button disabled={!permissions.adminStaffAndSuper}
                className={record.status.code === 'not-applicable' ? 'selected' : 'notSelected'}
                onClick={() => updateSubStatus(record, 'not-applicable')}
              >
                N/A
              </button>
            </div>
          );
        }
      },
    },
    // {
    //   title: 'Assignment/Note',
    //   key: 'assignmentOrNote',
    //   render: (_, record) => {
    //     if (record.isSubtask) {
    //       return record.assignment || '-';
    //     }
    //     return null;
    //   },
    // },
    // permissions.writeChild && {
    //   title: (
    //     <Button type="primary" icon={<PlusSvg fontSize="12" />} onClick={() => openAddModal(true)}>
    //       Add
    //     </Button>
    //   ),
    //   key: 'action',
    //   align: 'right',
    //   render: (_, record) => {
    //     if (record.isSubtask) {
    //       return (
    //         <Button type="link" onClick={() => setSelectedTask(record)}>
    //           Edit
    //         </Button>
    //       );
    //     }
    //     return (
    //       <Button type="link" onClick={() => setSelectedTask(record)}>
    //         Edit
    //       </Button>
    //     );
    //   },
    // },
  ].filter((item) => item);

  return (
    <>
      <Table
        columns={columns}
        dataSource={transformedData}
        rowKey={(record) => (record.isSubtask ? `sub-${record.childSubTaskId}` : `task-${record.childTaskId}`)}
        loading={isFetching || isLoading}
        pageInfo={transformedData}
        pagination={{ pageSize: 15 }}
      />
      <AddModal childId={childId} open={isAddModalOpen} refetch={refetch} onClose={() => openAddModal(false)} />
      <EditModal childId={childId} task={selectedTask} refetch={refetch} onClose={() => setSelectedTask()} />
    </>
  );
};

export default memo(TaskList);
